import React from 'react';
import { 
  Autocomplete, 
  TextField, 
  Typography, 
  Box,
  Chip,
  AutocompleteProps
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FiBookOpen } from 'react-icons/fi';
import { TransactionTemplate } from '../../../../types/journalEntry';
import { TRANSACTION_TEMPLATES } from '../../../../constants/transactionTemplates';
import { BasePaper } from './styles/SharedStyles';

interface TransactionTemplateSelectorProps {
  selectedTemplate: TransactionTemplate | null;
  onTemplateSelect: (template: TransactionTemplate | null) => void;
}

// Styled Components
const HeroContainer = styled(BasePaper)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgb(26, 31, 46) 0%, rgb(34, 39, 58) 100%)',
  marginBottom: theme.spacing(4),
  '&::before': {
    height: '3px',
    opacity: 1
  }
}));

// Create a typed version of Autocomplete for TransactionTemplate
const TypedAutocomplete = Autocomplete as React.ComponentType<AutocompleteProps<TransactionTemplate, false, false, false>>;

const StyledAutocomplete = styled(TypedAutocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(8px)',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    '&:hover': {
      border: '1px solid rgba(255, 255, 255, 0.2)',
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      border: '1px solid #0EA5E9',
      boxShadow: '0 0 0 2px rgba(14, 165, 233, 0.2)',
      background: 'rgba(255, 255, 255, 0.05)',
    }
  },
  '& .MuiAutocomplete-input': {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '1rem',
    fontWeight: 500,
  }
}));

const TemplateOption = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  width: '100%',
  transition: 'all 0.2s ease',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    transform: 'translateX(4px)',
  }
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.15), rgba(16, 185, 129, 0.15))',
  color: '#0EA5E9',
  border: '1px solid rgba(14, 165, 233, 0.3)',
  borderRadius: '16px',
  height: '24px',
  marginTop: theme.spacing(1),
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 2px 8px rgba(14, 165, 233, 0.2)',
  }
}));

export const TransactionTemplateSelector: React.FC<TransactionTemplateSelectorProps> = ({
  selectedTemplate,
  onTemplateSelect
}) => {
  // Use the imported TRANSACTION_TEMPLATES directly
  const templates = TRANSACTION_TEMPLATES;

  return (
    <HeroContainer>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <FiBookOpen size={24} color="#0EA5E9" />
          <Typography 
            variant="h4" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 600,
              letterSpacing: '-0.02em',
            }}
          >
            Create Journal Entry
          </Typography>
        </Box>
        
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            mb: 3,
            fontWeight: 400,
          }}
        >
          Start with a template or create a custom entry
        </Typography>

        <StyledAutocomplete
          options={templates}
          getOptionLabel={(option) => option.name}
          value={selectedTemplate}
          onChange={(_, newValue) => {
            onTemplateSelect(newValue);
          }}
          isOptionEqualToValue={(option, value) => 
            option.id === value?.id
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select a template for common transactions"
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <TemplateOption>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 500 
                  }}
                >
                  {option.name}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.6)',
                    mt: 0.5 
                  }}
                >
                  {option.description}
                </Typography>
                {option.type !== 'standard' && (
                  <CategoryChip 
                    label={option.type} 
                    size="small"
                  />
                )}
              </TemplateOption>
            </li>
          )}
        />
      </Box>
    </HeroContainer>
  );
};

export default TransactionTemplateSelector; 